// ---------------------------------------------------------------------------
// Common FastAPI error handling for the whole admin panel.
//
// IMPORTANT — this module NEVER surfaces backend-provided text:
//   - error.message
//   - response.data.message / .error / .error_value / .detail
//   - backend exception strings
//
// It looks only at the HTTP status code (or an already-unwrapped response body
// carrying `status_code`) plus known client-side conditions, and returns a
// fixed, frontend-controlled message.
//
// Auth (401), permission (403) and rate-limit (429) responses are still
// toasted centrally by the FastAPI response interceptor (Fast_interceptor.js).
// This helper is for the per-page catch / result-check blocks.
// ---------------------------------------------------------------------------

export const FAST_API_FALLBACK_MESSAGE =
  "Something went wrong. Please try again.";

/**
 * Pull an HTTP status code out of either an Axios error or an already-unwrapped
 * FastAPI response body. Returns a Number, or null when none is present.
 */
export const getFastApiErrorStatus = (value) => {
  const status =
    value?.response?.status ??
    value?.status_code ??
    value?.status ??
    null;

  if (status === null || status === undefined) return null;

  const numeric = Number(status);

  return Number.isNaN(numeric) ? null : numeric;
};

/**
 * True when `value` represents a failed FastAPI call — an Axios error, or an
 * unwrapped wrapper result that carries an error marker / 4xx-5xx status.
 *
 * Note: callers that special-case rate limiting (`res.rateLimited`) should do
 * that check first; this returns true for those too.
 */
export const isFastApiError = (value) => {
  if (!value) return false;

  if (value instanceof Error) return true;

  if (
    value.rateLimited === true ||
    value.success === false ||
    Boolean(value.error) ||
    Boolean(value.error_value) ||
    Boolean(value.error_code)
  ) {
    return true;
  }

  const status = getFastApiErrorStatus(value);

  return status !== null && status >= 400;
};

/**
 * Map a FastAPI failure (Axios error or unwrapped body) to a safe, fixed
 * user-facing message. `fallback` is returned only when nothing else matches.
 */
export const getFastApiErrorMessage = (
  value,
  fallback = FAST_API_FALLBACK_MESSAGE
) => {
  // Network / server unreachable
  if (
    value?.code === "ERR_NETWORK" ||
    (value?.request && !value?.response)
  ) {
    return (
      "Unable to connect to the server. " +
      "Please check your internet connection."
    );
  }

  // Use validation/detail message when available
  const msg = value?.errors?.details?.[0]?.msg;

  if (typeof msg === "string" && msg.trim()) {
    return msg.trim();
  }

  const status = getFastApiErrorStatus(value);

  if (status === 429) {
    return (
      "Too many requests. " +
      "Please wait a moment before trying again."
    );
  }

  if (status === 413) {
    return (
      "The file is too large. " +
      "Please upload a smaller file."
    );
  }

  if (status === 401) {
    return (
      "Your session has expired. " +
      "Please sign in again."
    );
  }

  if (status === 403) {
    return "You don't have permission to perform this action.";
  }

  if (status === 404) {
    return "The requested resource could not be found.";
  }

  if (status === 409) {
    return (
      "This item is currently being processed. " +
      "Please try again shortly."
    );
  }

  if (status >= 500 && status <= 599) {
    return (
      "Our server is currently busy. " +
      "Please try again in a few moments."
    );
  }

  if (status >= 400 && status <= 499) {
    return (
      "We couldn't complete this request. " +
      "Please check your input and try again."
    );
  }

  return fallback;
};